// §I · COVER — snapped from R10 tokens (.cv-* classes) and the standalone `cover()`
// markup (canon F4), verbatim. Fixed brand copy + N-derived subline render live; the
// silhouette is LIVE from the engine (mean of seven per stage). Stage dates drive the
// left silhouette label's arc, the footer arc range, and the chart year axis. Wizard-
// only slots (the reading date) stay placeholders until Track B.
//
// Every field carries its Field Mapping Spec `data-field` marker (15 total) — that is
// the traceability contract the mapping spec + parity tester rely on.

import SilhouetteChart from '../SilhouetteChart.jsx';
import { filled } from '../../insights/input.js';
import { fmtDocDate } from '../docDate.js';

const SLOT = { 'data-placeholder': 'true' };

// verbatim CD NUM_WORD (subline spells the stage count; wizard enforces 2–15, so no
// "number missing" branch exists — the ?? fallback never fires, kept as a guard).
const NUM_WORD = {
  1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight',
  9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
};

// §I is fed through the same seam as §II–§VII: everything below is already derived by
// `viewI(passport)` in viewFromPassport.js — stage meta validated, arc anchored, current
// index resolved. The renderer reads the view and nothing else (guard: seam.test.js).
export default function SectionI({ view }) {
  const {
    stageCount: stages, geometryClass, silhouette, tickLabels,
    curIdx, dated, arcShort, arcYears, arcShortLabel,
  } = view;

  return (
    <div className="cv-sheet">
      <div className="cv-top">
        <div className="cv-eyebrow" data-field="section_header">
          <span className="cv-dot" />
          <em>life map</em>
          <span className="cv-sep">·</span>
          <span>the reading</span>
        </div>
        {/* Дата документа = дата, когда он СГЕНЕРИРОВАН. Решение Кирилла 21.08, и оно намеренно
            узкое: никакой механики устаревания вокруг него не строить — ни порогов давности, ни
            «reading as of», ни пересчёта при позднем открытии. Паспорт — фикс-PDF, данные в нём
            цементируются, через два года он неактуален, и это заложено, а не дефект. Слот просто
            штампуется один раз при сборке (Track B). */}
        <div className="cv-badge" data-field="first_reading_badge">
          <span className="cv-badge-k">first reading</span>
          {view.generatedAt
            ? <span className="cv-badge-v">{fmtDocDate(view.generatedAt)}</span>
            : <span className="cv-badge-v" {...SLOT}>—</span>}
        </div>
      </div>

      <div className="cv-title-block">
        <h1 className="cv-title" data-field="h1" aria-label="this is your life. it is always moving.">
          <span className="cv-line cv-line-1"><span className="cv-w-it">this is</span></span>
          <span className="cv-line cv-line-2"><span className="cv-w-it cv-w-amber">your life.</span></span>
          <span className="cv-line cv-line-3"><span className="cv-w-tilt">it is</span> <span className="cv-w-it">always</span></span>
          <span className="cv-line cv-line-4"><span className="cv-w-ember">moving.</span></span>
        </h1>
        <p className="cv-sub" data-field="subline">
          A reading of <em>{NUM_WORD[stages] ?? stages} stages</em> of one life, drawn from your own words.
        </p>
      </div>

      <div className="cv-stage">
        <div className="cv-stage-eyebrow">
          <span data-field="silhouette_label_left" style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
            <span className="cv-eyb-k">silhouette</span>
            <span className="cv-eyb-sep">·</span>
            <span className="cv-eyb-v">{stages} stages</span>
            {dated && !arcShort && (
              <>
                <span className="cv-eyb-sep">·</span>
                <span className="cv-eyb-k">arc</span>
                <span className="cv-eyb-v">{arcShortLabel}</span>
              </>
            )}
          </span>
          <span className="cv-eyb-spacer" />
          <span data-field="silhouette_label_right" style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
            <span className="cv-eyb-k">mean of your layers</span>
            <span className="cv-eyb-sep">·</span>
            <span className="cv-eyb-k cv-eyb-mute">unsmoothed</span>
          </span>
        </div>
        <div className="cv-chart">
          <span className="cv-corner tl" />
          <span className="cv-corner tr" />
          <span className="cv-corner bl" />
          <span className="cv-corner br" />
          {/* cover peak/line reads through filled() (s836.d3): пустой этап = null; интерп даёт
              форму, не 0-line-ложь. filled НЕ покидает читателя — view.silhouette остаётся сырым.
              На all-touched корпусе null нет → filled() тождественно, §I байт-стабилен. */}
          <SilhouetteChart silhouette={filled(silhouette)} currentIdx={curIdx} geometryClass={geometryClass} tickLabels={tickLabels} />
        </div>
      </div>

      <div className="cv-bottom">
        <div className="cv-meta">
          <div className="cv-meta-row" data-field="recorded_date">
            <span className="cv-meta-k">recorded</span>
            {view.generatedAt
              ? <span className="cv-meta-v">{fmtDocDate(view.generatedAt)}</span>
              : <span className="cv-meta-v" {...SLOT}>— · — · —</span>}
          </div>
          <div className="cv-meta-row" data-field="arc_label">
            <span className="cv-meta-k">arc</span>
            <span className="cv-meta-v">
              {stages} stages
              {/* canon F4: arcYears null → nothing here (open-start / no dates),
                  NOT a placeholder — a moss dash on the cover reads as broken. */}
              {arcYears && <span className="cv-meta-mono">{arcYears}</span>}
            </span>
          </div>
        </div>
        <div className="cv-cta">
          <div className="cv-cta-kicker">next →</div>
          <a className="cv-cta-link" data-field="cta_link" href="https://authormode.co">authormode.co</a>
        </div>
      </div>
    </div>
  );
}
